import { EventDispatcher } from '../../events/event-dispatcher';
import {
	calculateOverlayElementPosition,
	calculateOverlayPoint,
	calculatePosition,
	ConnectionPositionPair,
	getScrollVisibility,
	OriginConnectionPosition,
	OverlayConnectionPosition,
	OverlayPoint,
	ScrollingVisibility,
} from '../../viewport/calculate-overlay';
import { Overlay } from '../overlay';
import { PositionStrategy } from './position-strategy';

export interface ConnectedOverlayPositionChange {
	connectionPair: ConnectionPositionPair;
	scrollableViewProperties?: ScrollingVisibility;
}

export class ConnectedPositionStrategy implements PositionStrategy {
	onPositionChange = new EventDispatcher<ConnectedOverlayPositionChange>();
	private offsetX = 0;
	private offsetY = 0;
	private scrollables: HTMLElement[] = [];
	private preferredPositions: ConnectionPositionPair[] = [];
	private origin: HTMLElement;
	private pane: HTMLElement | undefined;
	private lastConnectedPosition: ConnectionPositionPair | undefined;
	private applied = false;
	private positionLocked = false;
	private portal: HTMLElement;
	private updateTransformOrigin = false;

	constructor(private connectedTo: HTMLElement) {
		this.origin = this.connectedTo;
	}

	get positions() {
		return this.preferredPositions;
	}

	attach(overlayRef: Overlay): void {
		this.pane = overlayRef.overlayElement;
		this.portal = overlayRef.portalElement;
		window.addEventListener('resize', this.handleResizeChange);
	}

	detach() {
		this.applied = false;
		window.removeEventListener('resize', this.handleResizeChange);
	}

	dispose() {
		this.detach();
	}

	apply() {
		let overlayPoint: OverlayPoint;
		let overlayPosition: ConnectionPositionPair;
		if (this.applied && this.positionLocked && this.lastConnectedPosition) {
			overlayPosition =
				this.lastConnectedPosition || this.preferredPositions[0];

			overlayPoint = calculateOverlayPoint(
				this.origin.getBoundingClientRect(),
				this.pane.getBoundingClientRect(),
				overlayPosition,
				this.offsetX,
				this.offsetY
			);
		} else {
			this.applied = true;

			const { isFallback, position, point } = calculatePosition(
				this.origin.getBoundingClientRect(),
				this.pane.getBoundingClientRect(),
				this.preferredPositions,
				this.offsetX,
				this.offsetY
			);

			overlayPosition = position;
			overlayPoint = point;

			if (!isFallback) {
				this.lastConnectedPosition = position;
			}
		}

		const { x, y, verticalStyleProperty, horizontalStyleProperty } =
			calculateOverlayElementPosition(
				this.pane.getBoundingClientRect(),
				overlayPoint,
				overlayPosition
			);

		this.pane.style.top = '';
		this.pane.style.bottom = '';
		this.pane.style.left = '';
		this.pane.style.right = '';

		this.pane.style[verticalStyleProperty] = `${y}px`;
		this.pane.style[horizontalStyleProperty] = `${x}px`;

		this.onPositionChange.dispatch({
			connectionPair: overlayPosition,
			scrollableViewProperties: getScrollVisibility(
				this.pane,
				this.origin,
				this.scrollables
			),
		});

		if (this.updateTransformOrigin) {
			const originX = overlayPosition.overlayX !== 'start' ? 'right' : 'left';
			const originY = overlayPosition.overlayY === 'top' ? 'top' : 'bottom';
			this.portal.style.transformOrigin = `${originX} ${originY}`;
		}
	}

	withScrollableContainers(scrollables: HTMLElement[]) {
		this.scrollables = scrollables;
		return this;
	}

	withFallbackPosition(
		originPos: OriginConnectionPosition,
		overlayPos: OverlayConnectionPosition,
		offsetX?: number,
		offsetY?: number
	) {
		this.preferredPositions.push(
			new ConnectionPositionPair(originPos, overlayPos, offsetX, offsetY)
		);
		return this;
	}

	withOffsetX(offset: number) {
		this.offsetX = offset;
		return this;
	}

	withOffsetY(offset: number) {
		this.offsetY = offset;
		return this;
	}

	withLockedPosition(isLocked: boolean) {
		this.positionLocked = isLocked;
		return this;
	}

	withPositions(positions: ConnectionPositionPair[]) {
		this.preferredPositions = positions.slice();
		return this;
	}

	withTransformOriginUpdate() {
		this.updateTransformOrigin = true;
	}

	setOrigin(origin: HTMLElement) {
		this.origin = origin;
		return this;
	}

	private handleResizeChange = () => {
		this.apply();
	};
}
